'use client';

import { ScrollTopButton } from '@packages/modules/src/ScrollTopButton/ScrollTopButton';
import { AppBar, Box, Container } from '@packages/shared';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useIntersectionObserver } from '@packages/shared/src/hooks/useIntersectionObserver/useIntersectionObserver';
import React, { useEffect, useRef, useState } from 'react';

type StickyAreaProps = {
  /**
   * Children react element.
   * You need to pass it **two** `children` which will be displayed in it.
   * */
  children: React.ReactElement<any>[];
};

/**
 * This component is fixed on the bottom of the viewport. You need to pass it **two** `children` which will be displayed in it.*
 * The `StickyArea` uses the `ImageGallery` as ref - on **mobile** and **tablet** the first child will transition in, if the `ImageGallery` is not in the viewport.
 * On **desktop** the whole `StickyArea` is **only visible** if the `ImageGallery` is out of viewport.
 * */
export const StickyArea = ({ children }: StickyAreaProps) => {
  const { isDesktop } = useDeviceType();
  const attachedNodeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    attachedNodeRef.current = document.getElementById('image-gallery');
  }, []);

  // Is intialized with true, because the StickyArea should not transition in directly after it is mounted.
  const [isIntersecting, setIsIntersecting] = useState(true);
  useIntersectionObserver(attachedNodeRef, {
    callback: (isInview) => setIsIntersecting(!!isInview),
    intersectOnce: false,
  });

  return (
    <AppBar
      position="sticky"
      sx={[
        {
          top: 'auto',
          bottom: 0,
          backgroundColor: 'common.white',
          px: { xs: 1.5, md: 3, lg: 0 },
          pb: { xs: 1, md: 1, lg: 3.5 },
          pt: { xs: 1.5, md: 1, lg: 3.5 },
        },
        isDesktop && {
          transform: isIntersecting ? 'translateY(100%)' : 'translateY(0px)',
          opacity: isIntersecting ? 0 : 1,
          transition: (theme) =>
            theme.transitions.create(['transform', 'opacity'], {
              duration: theme.transitions.duration.shortest,
            }),
        },
      ]}
    >
      <Container
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          justifyContent: 'space-between',
          alignItems: { lg: 'center' },
        }}
      >
        {
          /**
           * animation should only be done, if device !== "desk",
           * otherwise the `Image`, `Price` and `TaxHint` should always be visible
           * */

          !isDesktop ? (
            <Box
              sx={{
                maxHeight: isIntersecting ? 0 : '200px',
                opacity: isIntersecting ? 0 : 1,
                overflow: 'hidden',
                transition: (theme) =>
                  theme.transitions.create(['all'], {
                    // TODO replace standard with longest when longest is implemented
                    duration: theme.transitions.duration.standard,
                  }),
              }}
            >
              <Box
                sx={{
                  pb: 1.5,
                  boxShadow: '0 -4px 7px -5px rgb(0 0 0 / 20%)',
                  backgroundColor: 'common.white',
                }}
              >
                {children[0]}
              </Box>
            </Box>
          ) : (
            children[0]
          )
        }
        {children[1]}
      </Container>
      <Box
        sx={{
          position: 'absolute',
          top: (theme) => `-${theme.spacing(2)}`,
          right: (theme) => theme.spacing(1),
          transform: 'translateY(-100%)',
        }}
      >
        <ScrollTopButton />
      </Box>
    </AppBar>
  );
};
